import React, { useState } from "react";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";

// Single-line address field for an extra stop/leg. Combines three ways to
// fill it in, same as the main Pick-Up/Drop-Off fields:
//   1. Search the address book (saved locations) — dropdown of matches as you type.
//   2. Search Google Maps — AddressAutocomplete suggestions.
//   3. Type the full address manually — always works, e.g. for a new area
//      that hasn't shown up on Google Maps yet.
// Picking a saved address also hands back its contact name/phone so the
// caller can pre-fill the stop's contact fields.
function formatSavedAddr(a) {
  const parts = [a.line1, a.line2, a.suburb, a.state, a.postcode].filter(Boolean).join(", ");
  return a.label?.trim() ? `${a.label.trim()} — ${parts}` : parts;
}

export default function StopAddressField({ value, onChange, onResolved, onPickSaved, saved = [], placeholder, className, testid, ...rest }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = (q
    ? saved.filter((a) =>
        (a.label || "").toLowerCase().includes(q) ||
        (a.line1 || "").toLowerCase().includes(q) ||
        (a.suburb || "").toLowerCase().includes(q))
    : saved
  ).slice(0, 8);

  return (
    <div className="relative">
      <AddressAutocomplete
        value={value}
        onChange={(v) => { onChange(v); setOpen(true); }}
        onResolved={(p) => { onResolved ? onResolved(p) : onChange(p.formatted || p.line1); setOpen(false); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || "Search saved address book, search Google Maps, or type the address manually"}
        className={className}
        data-testid={testid}
        {...rest}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[#15151f] border border-white/12 rounded-xl shadow-xl max-h-64 overflow-auto">
          <p className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-white/35 border-b border-white/5">Saved addresses</p>
          {matches.map((a) => (
            <button
              key={a.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(formatSavedAddr(a));
                onPickSaved && onPickSaved(a);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0"
            >
              <div className="text-sm text-white truncate">{a.label || a.line1}</div>
              <div className="text-xs text-white/50 truncate">
                {[a.line1, a.line2, a.suburb, a.state, a.postcode].filter(Boolean).join(", ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
